import type { IUiThreeDPresentation } from './Interfaces';
import * as THREE from 'three';
import type { IImageSliceViewModel, IRaycastable, Intersection } from '@/engine/app/interfaces';
import type { IPresentationChangeHandler } from './Interfaces';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { SlicePositionInfo, SliceOrientationInfo } from '@/engine/app/modules/images/ImageStackController';
import type { SliceType } from '@/engine/app/modules/images/interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { IImagePresentationPlaneStrategy } from './PresentationVisibilityStrategies';
import { XRayEffect } from '../app/modules/images/ImageStack/ImageStack';

export class ImagePresentation implements IUiThreeDPresentation, IRaycastable {
  private _planeTexture: THREE.Texture;

  private _planeGeometry: THREE.BufferGeometry;

  private _planeMaterial: THREE.ShaderMaterial;

  private _planeMesh: THREE.Mesh;

  private _imagesService: ImagesService;

  private _sliceType: SliceType;

  private _sliceOrientationInfo: SliceOrientationInfo | undefined;

  private _changeHandler: IPresentationChangeHandler;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private _planeStrategy: IImagePresentationPlaneStrategy;

  public constructor(
    imagesService: ImagesService,
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler,
    planeStrategy: IImagePresentationPlaneStrategy
  ) {
    this._imagesService = imagesService;
    this._sliceType = imageModel.descriptor;

    this._planeTexture = new THREE.Texture();
    this._planeGeometry = new THREE.BufferGeometry();
    this._planeMaterial = this._createMaterial();
    this._planeMesh = new THREE.Mesh(this._planeGeometry, this._planeMaterial);
    this._planeMesh.matrixAutoUpdate = false;
    this._planeMesh.renderOrder = -1; // images plane mesh should be rendered very first
    this._planeMesh.visible = imageModel.isVisible;
    this._planeStrategy = planeStrategy;
    this._planeStrategy.onPlaneCreated(this._planeMesh, imageModel, changeHandler); // different behaviors for 2d and 3d scenes: 2d - always visible, 3d - subscribe to imageModel.isVisible

    this._changeHandler = changeHandler;

    this._subscriptions.add(
      this._imagesService.subscribeToSliceOrientationChange(
        this._sliceType,
        this._handleSliceOrientationChange.bind(this)
      )
    );
    this._subscriptions.add(
      this._imagesService.subscribeToSlicePositionChange(this._sliceType, this._handleSlicePositionChange.bind(this))
    );
    this._subscriptions.add(
      this._imagesService.subscribeToSliceVisualizationChange(
        this._sliceType,
        this._handleSliceVisualizationChange.bind(this)
      )
    );
  }

  public dispose(): void {
    this._planeStrategy.dispose();
    this._subscriptions.dispose();
    this._planeMaterial.dispose();
    this._planeGeometry.dispose();
    this._planeTexture.dispose();
  }

  public get3dObject(): THREE.Object3D {
    return this._planeMesh;
  }

  public rayCast(origin: THREE.Vector3, direction: THREE.Vector3): Array<Intersection> {
    if (!this._planeMesh.visible) return [];
    const ret: Array<Intersection> = [];
    const intersections: Array<THREE.Intersection<THREE.Object3D<THREE.Object3DEventMap>>> = [];
    this._planeMesh.raycast(new THREE.Raycaster(origin, direction), intersections);
    intersections.forEach((intersection: THREE.Intersection<THREE.Object3D<THREE.Object3DEventMap>>): void => {
      ret.push({
        point: intersection.point,
        distance: intersection.distance,
        descriptor: this._sliceType
      });
    });
    return ret;
  }

  private _handleSliceOrientationChange(orientationInfo: SliceOrientationInfo): void {
    this._planeTexture.dispose();
    this._planeTexture = new THREE.CanvasTexture(orientationInfo.canvas);
    this._planeTexture.magFilter = THREE.LinearFilter;
    this._planeTexture.minFilter = THREE.LinearMipmapLinearFilter;

    this._planeMaterial.uniforms['imageTexture'].value = this._planeTexture;

    this._planeGeometry.dispose();
    this._planeGeometry = new THREE.PlaneGeometry(
      orientationInfo.volumeDimensions.x,
      orientationInfo.volumeDimensions.y,
      1,
      1
    );

    this._sliceOrientationInfo = orientationInfo;

    this._planeMesh.material = this._planeMaterial;
    this._planeMesh.geometry = this._planeGeometry;

    if (orientationInfo.xRaySlicerParams?.xRayEffect === XRayEffect.Addition) {
      const box = new THREE.BoxGeometry(
        orientationInfo.volumeDimensions.x,
        orientationInfo.volumeDimensions.y,
        orientationInfo.sliceThickness,
        1,
        1,
        1
      );
      this._planeStrategy.onPlaneChanged(box);
    } else {
      this._planeStrategy.onPlaneChanged(this._planeGeometry);
    }
  }

  private _handleSlicePositionChange(positionInfo: SlicePositionInfo): void {
    if (!this._sliceOrientationInfo) throw new Error('Slice Orientation Info must be obtained by this point');
    this._planeMesh.matrix = positionInfo.sliceCS.clone();
    const pos = new THREE.Vector3(
      this._sliceOrientationInfo.volumeDimensions.x / 2,
      this._sliceOrientationInfo.volumeDimensions.y / 2,
      0
    ).applyMatrix4(this._planeMesh.matrix);
    this._planeMesh.matrix.setPosition(pos);
    this._planeMesh.updateMatrixWorld(true);
  }

  private _handleSliceVisualizationChange(): void {
    this._planeTexture.needsUpdate = true;
    if (this._planeMesh.visible) this._changeHandler.handlePresentationChange();
  }

  private _createMaterial(): THREE.ShaderMaterial {
    const material = new THREE.ShaderMaterial({
      vertexShader:
        '#if NUM_CLIPPING_PLANES > 0 \n\
            varying vec3 vClipPosition; \n\
          #endif \n\
          varying vec2 vUv; \n\
          void main() \n\
          { \n\
            vUv = uv; \n\
            vec3 transformed = vec3( position ); \n\
            gl_Position = projectionMatrix * modelViewMatrix * vec4 (position, 1.0);\n\
            vec4 mvPosition = modelViewMatrix * vec4( transformed, 1.0 ); \n\
            gl_Position = projectionMatrix * mvPosition; \n\
            #if NUM_CLIPPING_PLANES > 0 \n\
              vClipPosition = - mvPosition.xyz; \n\
            #endif \n\
          }',
      fragmentShader:
        'uniform sampler2D imageTexture; \n\
          #if NUM_CLIPPING_PLANES > 0 \n\
            varying vec3 vClipPosition; \n\
            uniform vec4 clippingPlanes[ NUM_CLIPPING_PLANES ]; \n\
          #endif \n\
          varying vec2 vUv; \n\
          void main() \n\
          { \n\
            #if NUM_CLIPPING_PLANES > 0 \n\
              vec4 plane; \n\
              float d, v; \n\
              #pragma unroll_loop_start \n\
              for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) { \n\
                plane = clippingPlanes[ i ]; \n\
                d = dot( vClipPosition, plane.xyz ); \n\
                v = abs(d - plane.w); \n\
                if (v > 0.01 && d > plane.w) \n\
                  discard; \n\
              } \n\
              #pragma unroll_loop_end \n\
              #if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES \n\
                bool clipped = true; \n\
                #pragma unroll_loop_start \n\
                for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) { \n\
                  plane = clippingPlanes[ i ]; \n\
                  d = dot( vClipPosition, plane.xyz ); \n\
                  v = abs(d - plane.w); \n\
                  clipped = ( v > 0.01 ) && ( d > plane.w ) && clipped; \n\
                } \n\
                #pragma unroll_loop_end \n\
                if ( clipped ) discard; \n\
              #endif \n\
            #endif \n\
            gl_FragColor = texture(imageTexture, vUv); \n\
          }',
      uniforms: {
        imageTexture: { value: this._planeTexture }
      },
      clipping: true,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1
    });

    return material;
  }
}
